"use client"

import * as React from "react";
import type * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Minus, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";

export function supportsHiddenUntilFound(): boolean {
  return (
    typeof HTMLElement !== "undefined" && "onbeforematch" in HTMLElement.prototype
  )
}

type AccordionRootProps = React.ComponentProps<typeof AccordionPrimitive.Root>

type AccordionContextValue = {
  type: "single" | "multiple"
  collapsible: boolean
  disabled: boolean
  openValues: string[]
  toggleItem: (value: string) => void
  openItem: (value: string) => void
  isOpen: (value: string) => boolean
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null)

type AccordionItemContextValue = {
  value: string
  disabled: boolean
  triggerId: string
  panelId: string
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null)

function useAccordionContext(): AccordionContextValue {
  const context = React.useContext(AccordionContext)
  if (!context) {
    throw new Error("Accordion compound components must be used within <Accordion>")
  }
  return context
}

function useAccordionItemContext(): AccordionItemContextValue {
  const context = React.useContext(AccordionItemContext)
  if (!context) {
    throw new Error("AccordionTrigger and AccordionContent must be used within <AccordionItem>")
  }
  return context
}

function useOpenValues(
  type: "single" | "multiple",
  valueProp: string | string[] | undefined,
  defaultValue: string | string[] | undefined,
  onValueChange: ((value: string) => void) | ((value: string[]) => void) | undefined
): [string[], (next: string[]) => void] {
  const normalizeDefault = (): string[] => {
    if (type === "single") {
      const singleDefault = defaultValue as string | undefined
      return singleDefault ? [singleDefault] : []
    }
    return (defaultValue as string[] | undefined) ?? []
  }

  const [uncontrolledOpenValues, setUncontrolledOpenValues] =
    React.useState<string[]>(normalizeDefault)

  const isControlled = valueProp !== undefined
  const openValues = isControlled
    ? type === "single"
      ? valueProp
        ? [valueProp as string]
        : []
      : (valueProp as string[])
    : uncontrolledOpenValues

  const setOpenValues = useCallback(
    (next: string[]) => {
      if (!isControlled) {
        setUncontrolledOpenValues(next)
      }
      if (type === "single") {
        (onValueChange as ((value: string) => void) | undefined)?.(next[0] ?? "")
      } else {
        (onValueChange as ((value: string[]) => void) | undefined)?.(next)
      }
    },
    [isControlled, onValueChange, type]
  )

  return [openValues, setOpenValues]
}

function Accordion(props: AccordionRootProps) {
  const {
    type,
    value: valueProp,
    defaultValue,
    onValueChange,
    disabled = false,
    className,
    onKeyDown,
    collapsible: collapsibleProp,
    ...domProps
  } = props as AccordionRootProps & { collapsible?: boolean }
  const collapsible = type === "single" ? (collapsibleProp ?? false) : false
  const rootRef = useRef<HTMLDivElement>(null)
  const [openValues, setOpenValues] = useOpenValues(
    type,
    valueProp,
    defaultValue,
    onValueChange as ((value: string) => void) | ((value: string[]) => void) | undefined
  )

  const toggleItem = useCallback(
    (itemValue: string) => {
      const isItemOpen = openValues.includes(itemValue)
      if (type === "single") {
        if (isItemOpen) {
          if (collapsible) {
            setOpenValues([])
          }
        } else {
          setOpenValues([itemValue])
        }
        return
      }
      if (isItemOpen) {
        setOpenValues(openValues.filter((v) => v !== itemValue))
      } else {
        setOpenValues([...openValues, itemValue])
      }
    },
    [collapsible, openValues, setOpenValues, type]
  )

  const openItem = useCallback(
    (itemValue: string) => {
      if (type === "single") {
        setOpenValues([itemValue])
        return
      }
      if (!openValues.includes(itemValue)) {
        setOpenValues([...openValues, itemValue])
      }
    },
    [openValues, setOpenValues, type]
  )

  const isOpen = useCallback(
    (itemValue: string) => openValues.includes(itemValue),
    [openValues]
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return

    const navigationKeys = ["ArrowDown", "ArrowUp", "Home", "End"]
    if (!navigationKeys.includes(event.key)) return

    const root = rootRef.current
    if (!root) return

    const triggers = Array.from(
      root.querySelectorAll<HTMLButtonElement>(
        '[data-slot="accordion-trigger"]:not(:disabled)'
      )
    )
    if (triggers.length === 0) return

    const currentIndex = triggers.indexOf(document.activeElement as HTMLButtonElement)
    let nextIndex = currentIndex

    if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = triggers.length - 1
    } else if (event.key === "ArrowDown") {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % triggers.length
    } else if (event.key === "ArrowUp") {
      nextIndex =
        currentIndex < 0 ? triggers.length - 1 : (currentIndex - 1 + triggers.length) % triggers.length
    }

    event.preventDefault()
    triggers[nextIndex]?.focus()
  }

  const contextValue = useMemo<AccordionContextValue>(
    () => ({
      type,
      collapsible,
      disabled,
      openValues,
      toggleItem,
      openItem,
      isOpen,
    }),
    [collapsible, disabled, isOpen, openItem, openValues, toggleItem, type]
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        ref={rootRef}
        data-slot="accordion"
        data-orientation="vertical"
        className={className}
        onKeyDown={handleKeyDown}
        {...domProps}
      />
    </AccordionContext.Provider>
  )
}

function AccordionItem({
  className,
  value,
  disabled: itemDisabled = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  const { disabled: rootDisabled, isOpen } = useAccordionContext()
  const baseId = useId()
  const triggerId = `${baseId}-trigger`
  const panelId = `${baseId}-panel`
  const open = isOpen(value)
  const disabled = rootDisabled || itemDisabled

  const itemContextValue = useMemo<AccordionItemContextValue>(
    () => ({
      value,
      disabled,
      triggerId,
      panelId,
    }),
    [disabled, panelId, triggerId, value]
  )

  return (
    <AccordionItemContext.Provider value={itemContextValue}>
      <div
        data-slot="accordion-item"
        data-state={open ? "open" : "closed"}
        data-orientation="vertical"
        data-disabled={disabled ? "" : undefined}
        className={cn("border-b", className)}
        {...props}
      />
    </AccordionItemContext.Provider>
  )
}

function AccordionTrigger({
  className,
  children,
  onClick,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  const { toggleItem, disabled: rootDisabled, isOpen } = useAccordionContext()
  const { value, disabled: itemDisabled, triggerId, panelId } = useAccordionItemContext()
  const open = isOpen(value)
  const disabled = rootDisabled || itemDisabled

  return (
    <div className="flex" data-orientation="vertical" data-state={open ? "open" : "closed"}>
      <button
        type="button"
        id={triggerId}
        data-slot="accordion-trigger"
        data-state={open ? "open" : "closed"}
        data-orientation="vertical"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        className={cn(
          "body-copy-l-bold group focus-visible:ring-primary flex flex-1 items-center justify-between gap-2 py-6 text-left text-foreground outline-none transition-all hover:no-underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
          className
        )}
        onClick={(event) => {
          onClick?.(event)
          if (!event.defaultPrevented && !disabled) {
            toggleItem(value)
          }
        }}
        {...props}
      >
        {children}
        <span
          className="relative inline-flex size-6 shrink-0 text-inherit"
          aria-hidden
        >
          <Plus className="pointer-events-none absolute inset-0 size-6 transition-opacity group-data-[state=open]:opacity-0" />
          <Minus className="pointer-events-none absolute inset-0 size-6 opacity-0 transition-opacity group-data-[state=open]:opacity-100" />
        </span>
      </button>
    </div>
  )
}

function AccordionContent({
  className,
  children,
  forceMount: _forceMount,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  const { openItem, isOpen } = useAccordionContext()
  const { value, triggerId, panelId } = useAccordionItemContext()
  const open = isOpen(value)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    if (open) {
      panel.removeAttribute("hidden")
    } else if (supportsHiddenUntilFound()) {
      panel.setAttribute("hidden", "until-found")
    } else {
      panel.setAttribute("hidden", "")
    }
  }, [open])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    const handleBeforeMatch = () => {
      openItem(value)
    }

    panel.addEventListener("beforematch", handleBeforeMatch)
    return () => panel.removeEventListener("beforematch", handleBeforeMatch)
  }, [openItem, value])

  return (
    <div
      data-slot="accordion-content"
      data-state={open ? "open" : "closed"}
      data-orientation="vertical"
      className="body-copy-l data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-foreground"
      {...props}
    >
      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        {...(!open && !supportsHiddenUntilFound() ? { hidden: true } : undefined)}
      >
        <div className={cn("pb-6", className)}>{children}</div>
      </div>
    </div>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
